import { MedplumClient, parseReference } from "@medplum/core";
import {
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import { BILLING_SECTION_LINK_ID } from "@/billing/components/VerifyInsurance/constants/verify-insurance-fields";
import type {
  InsuranceFormValues,
  InsuranceVerificationTier,
} from "@/billing/components/VerifyInsurance/types/verify-insurance";
import { insuranceFormValuesToBillingSectionFormValues } from "@/billing/components/VerifyInsurance/utils/insurance-form-values-to-questionnaire-values";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { setAccounts } from "@/utils/accounts";
import { getActiveOrganizationReference, joinReferenceString } from "@/utils/medplum";
import { pruneDisabledAnswers } from "@/utils/questionnaire-response";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

export const fetchClientBillingQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<[Questionnaire | undefined, QuestionnaireResponse | undefined]> => {
  const questionnaireResponseBundle = await medplum.searchOne("QuestionnaireResponse", {
    patient: joinReferenceString("Patient", clientId),
    identifier: Questionnaires.ClientIntake,
    _sort: "-_lastUpdated",
  });

  let query: Record<string, string>;
  if (questionnaireResponseBundle?.questionnaire) {
    query = {
      _id: parseReference({ reference: questionnaireResponseBundle.questionnaire })[1],
    };
  } else {
    query = {
      url: Questionnaires.ClientIntake,
      status: "active",
      _sort: "-_lastUpdated",
      _compartment: getActiveOrganizationReference(),
    };
  }

  const questionnaireBundle = await medplum.searchOne("Questionnaire", query);

  return [questionnaireBundle, questionnaireResponseBundle];
};

/**
 * Writes all Verify Insurance Step 2 (insurance) form values into the client's
 * intake QuestionnaireResponse for the given tier. Used when Step 2 saves so
 * insurance info lives only in the intake.
 * Returns the updated intake so the caller can update Redux and local state.
 */
export const syncInsuranceFieldsToIntake = async (
  medplum: MedplumClient,
  clientId: string,
  tier: InsuranceVerificationTier,
  sourceId: string,
  formValues: InsuranceFormValues,
): Promise<QuestionnaireResponse | undefined> => {
  const [intakeQuestionnaire, intakeQR] = await fetchClientBillingQuestionnaireResponse(
    medplum,
    clientId,
  );
  if (!intakeQuestionnaire?.id || !intakeQR) return undefined;

  const overlapValues = insuranceFormValuesToBillingSectionFormValues(formValues, tier);
  const subject: Reference<Patient> = { reference: joinReferenceString("Patient", clientId) };
  const source: Reference<Practitioner> = {
    reference: joinReferenceString("Practitioner", sourceId),
  };
  const updatedIntake = pruneDisabledAnswers(
    intakeQuestionnaire,
    getQuestionnaireResponseFromFormValues(
      intakeQuestionnaire,
      overlapValues,
      subject,
      source,
      intakeQR,
      null,
      BILLING_SECTION_LINK_ID,
    ),
  );

  const updatedIntakeResults = await medplum.updateResource(updatedIntake);
  await setAccounts(medplum, updatedIntakeResults, [], { extractFromPatient: true });
  return updatedIntakeResults;
};
